import React from 'react';
import { IonContent, IonPage, useIonAlert, useIonToast, useIonRouter } from '@ionic/react';
import { RouteComponentProps } from 'react-router';
import { Button } from 'react-bootstrap';
import { Form, Select, Input, Space } from 'antd';
import { axius, func, types } from 'utils';
import { IonLoading, PasswordStrength, Header } from 'components';

import './Auth.scss';

interface Props extends RouteComponentProps, types.StateProps { };
const Reset: React.FC<Props> = props => {

    const router = useIonRouter();

    const { _data: { settings, phones } } = props;

    const [form] = Form.useForm();

    const [vv, setValues]: any = React.useState({});
    const [step, setStep] = React.useState(1);
    const [strongPWD, setStrongPWD] = React.useState(false);
    const [phoneNumber, setPhoneNumber] = React.useState('');
    const [loading, setLoading] = React.useState('');
    const [presentAlert] = useIonAlert();
    const [presentToast] = useIonToast();

    const submit = (v: any) => {
        step === 1 ? submitSendCode(v) : submitReset(v);
    }

    const submitSendCode = (v: any) => {
        setLoading('sending code...');
        const username = `${phones[v.country.toLowerCase()].code}${v.phoneNumber.replace(/^0/, '')}`;
        axius.post('auth/reset', { username }).then((res: types.AxiusResponse) => {
            setLoading('');
            if (res.status === 201) {
                if (step === 1) {
                    setStep(2);
                    setPhoneNumber(v.phoneNumber);
                } else {
                    presentToast({ message: 'Your password has been changed. You can now login.', duration: 1500 });
                    router.push('/login', 'none', 'replace');
                }
            } else {
                if (res.status === 412) {
                    presentAlert({ message: res.data.join('<br />'), });
                } else {
                    presentAlert({ message: res.message, });
                }
            }
        });
    }

    const submitReset = async (v: any) => {
        if (strongPWD) {
            setLoading('pls wait...');
            v['username'] = phoneNumber;
            axius.put('auth/reset', v).then((res: types.AxiusResponse) => {
                setLoading('');
                if (res.status === 200) {
                    presentToast({ message: 'Your password has been changed. You can now login.', duration: 1500 });
                    props.history.replace('/login');
                } else {
                    if (res.status === 412) {
                        presentAlert({ message: res.data.join('<br />'), });
                    } else {
                        presentAlert({ message: res.message, });
                    }
                }
            });
        } else {
            presentAlert({ message: 'Please select a strong password', });
        }
    }

    const confirmPassword = (rule: any, value: any, callback: any) => {
        if (!value) {
            callback();
        }
        if (value !== form.getFieldValue('password')) {
            callback('The passwords provided don\'t match. Please enter them again.');
        }
        callback();
    };

    return (
        <IonPage id="Reset">
            <Header {...props} title="Reset your password" />

            <IonContent fullscreen={false}>
                <IonLoading open={loading ? true : false} message={loading} onCancel={() => setLoading('')} />
                <div className="mt-4 flex-middle" style={{ height: '100%' }}>
                    {step === 1 && (
                        <div style={{ width: '100vw' }}>
                            <h4 className="text-center bold mb-3 animate__animated animate__fadeIn"><b>Forgot Password?</b></h4>
                            <div className="text-center text-muted animate__animated animate__fadeIn">
                                Provide us with your phone number <br /> and we will help you change your password
                            </div>
                            <div>&nbsp;</div>
                            <div className="container">
                                <Form form={form} layout="vertical" onFinish={submit} style={{ width: '100%' }}>

                                    <div className="full-input animate__animated animate__fadeIn">
                                        <Form.Item>
                                            <Space.Compact style={{ width: '100%' }}>
                                                <Form.Item name="country" noStyle initialValue="NG" rules={[{ required: true, message: 'Country is required' }]}>
                                                    <Select style={{ width: '28vw' }} size="large" disabled={settings.countries.length === 1 ? true : false}>
                                                        {settings.countries.map((loc: any) => (
                                                            <Select.Option key={loc.code} value={loc.code}>
                                                                <small>
                                                                    <img
                                                                        src={`https://flagcdn.com/16x12/${loc.code.toLowerCase()}.png`}
                                                                        alt={loc.name}
                                                                    /> &nbsp;
                                                                    +{loc.callingCode}
                                                                </small>
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </Form.Item>
                                                <Form.Item name="phoneNumber" noStyle rules={[
                                                    { required: true, message: 'Phone number is required' },
                                                ]}>
                                                    <Input style={{ width: 'calc(100% - 28vw)' }} autoFocus type="number" size="large" placeholder="* Without country code" />
                                                </Form.Item>
                                            </Space.Compact>
                                        </Form.Item>
                                    </div>

                                    <p>&nbsp;</p>
                                    <div className="animate__animated animate__fadeIn">
                                        <Button type="submit" variant="primary btn-block btn-md">
                                            Next
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div style={{ width: '100vw' }}>
                            <h4 className="text-center bold mb-3 animate__animated animate__fadeIn">
                                <b>Verify your account</b>
                            </h4>
                            <p className="text-center text-muted animate__animated animate__fadeIn">
                                Enter the code sent to your phone number. <br />
                                Wrong number? <span className="primary" onClick={() => setStep(1)}>Change here</span>.
                            </p>
                            <div>&nbsp;</div>
                            <div className="container">
                                <Form form={form} layout="vertical" onFinish={submit} style={{ width: '100%' }}>
                                    <div className="full-input animate__animated animate__fadeIn">
                                        <Form.Item name="code" label="OTP code" rules={[{ required: true, message: 'This field is required' }]}>
                                            <Input autoComplete="off" size="large" type="text" placeholder="• • • • • • • •" />
                                        </Form.Item>
                                    </div>
                                    <div className="full-input animate__animated animate__fadeIn">
                                        <Form.Item name="password" label="New password" rules={[
                                            { required: true, message: 'This field is required' },
                                            { min: 8, message: 'Minimum of 8 charaters' },
                                        ]}>
                                            <Input
                                                type="password" autoComplete="off" id="rpwd" size="large"
                                                onChange={e => setValues({ ...vv, password: e.target.value })} placeholder="• • • • • • • •"
                                            />
                                        </Form.Item>
                                        <i className={`input-icon text-muted fa fa-eye`} onClick={() => func.togglePasswordShow('rpwd')} />
                                    </div>

                                    <div className="full-input animate__animated animate__fadeIn">
                                        <Form.Item name="passwordConfirm" label="Repeat new password" rules={[{ required: true, message: 'This field is required' }, { validator: confirmPassword }]}>
                                            <Input
                                                type="password" id="rcpwd" autoComplete="off" size="large"
                                                onChange={e => setValues({ ...vv, password: e.target.value })} placeholder="• • • • • • • •"
                                            />
                                        </Form.Item>
                                        <i className={`input-icon text-muted fa fa-eye`} onClick={() => func.togglePasswordShow('rcpwd')} />
                                    </div>
                                    <PasswordStrength {...props} value={vv.password} onChange={(strongPWD: boolean) => setStrongPWD(strongPWD)} />

                                    <p>&nbsp;</p>
                                    <p>&nbsp;</p>
                                    <div className="animate__animated animate__fadeIn">
                                        <Button type="submit" variant="primary btn-block btn-md">
                                            Verify & Reset
                                        </Button>
                                    </div>
                                </Form>
                            </div>
                        </div>
                    )}
                </div>
            </IonContent>
        </IonPage>
    );
}

export default Reset;